import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useArticles } from '../hooks/useArticles';
import { useTextSelection } from '../hooks/useTextSelection';
import { useHighlights } from '../hooks/useHighlights';
import { createArticleFeedback, getArticleFeedback } from '../services/articles';
import useNavigation from '../hooks/useNavigation';
import {
  shouldShowLanguageBadge,
  getLanguageName,
  getLanguageBadgeClasses,
  formatLanguageCode,
  getTextDirection
} from '../utils/language';
import { parseMarkdownSummary, hasMarkdownFormatting } from '../utils/markdown';
import { formatFullDate } from '../utils/dateUtils';
import ShareContextMenu from './ShareContextMenu';
import SharePreviewDialog from './SharePreviewDialog';
import RelatedArticlesWidget from './RelatedArticlesWidget';
import ImageDebugPanel from './ImageDebugPanel';
import HighlightsSidebar from './HighlightsSidebar';
import HighlightRenderer from './HighlightRenderer';

const ArticleViewer = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [feedback, setFeedback] = useState(null); // Current feedback state
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [localError, setLocalError] = useState(null); // Local error state for this article
  const [highlightsSidebarOpen, setHighlightsSidebarOpen] = useState(false);
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const { id } = useParams();
  const { goBack } = useNavigation();
  const location = useLocation();

  const {
    currentArticle,
    fetchArticle,
    removeArticle,
    regenerateArticleSummary,
    refetchArticleContent,
    markAsRead,
    markAsUnread,
    isLoading,
    isUpdating,
    isDeleting,
    isRegenerating,
    isRefetching,
    clearCurrentArticle,
    clearOperationStates,
  } = useArticles();

  // Text selection and share functionality
  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  // Highlights functionality
  // NOTE: We pass the raw summary for hash calculation, but we'll use rendered DOM text for positions
  const {
    highlights,
    isLoading: highlightsLoading,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    reattachHighlights,
  } = useHighlights(parseInt(id), currentArticle?.summary);

  // Fetch article on mount
  useEffect(() => {
    if (id) {
      // Clear local error and operation states from previous article
      setLocalError(null);
      clearOperationStates();

      fetchArticle(parseInt(id)).then((article) => {
        // Debug: Log debug_data status
        console.log('🔬 [ArticleViewer] Article fetched:', {
          id: article?.id,
          hasDebugData: !!article?.debug_data,
          debugDataType: typeof article?.debug_data,
          debugDataLength: Array.isArray(article?.debug_data) ? article.debug_data.length : 'not an array',
          debugDataSample: article?.debug_data ? JSON.stringify(article.debug_data).substring(0, 200) : 'null'
        });

        // Fetch existing feedback after article is loaded
        fetchExistingFeedback(parseInt(id));
      }).catch(error => {
        console.error('Error fetching article:', error);
        setLocalError(error.message);
        if (error.message.includes('404')) {
          goBack();
        }
      });
    }

    // Cleanup on unmount
    return () => {
      clearCurrentArticle();
      clearOperationStates();
      setFeedback(null);
      setLocalError(null);
    };
  }, [id, fetchArticle, goBack, clearCurrentArticle, clearOperationStates]);

  // Clear local errors when switching tabs
  useEffect(() => {
    if (localError) {
      setLocalError(null);
    }
  }, [activeTab]);

  // Scroll position memory (Issue #2 fix)
  // Save scroll position periodically and restore on mount
  useEffect(() => {
    const scrollKey = `article_scroll_${id}`;

    // Restore scroll position if coming back from navigation
    if (location.state?.restoredFromHistory) {
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        const scrollPos = parseInt(savedScroll, 10);
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        }, 100);
      }
    }

    // Save scroll position on scroll events (debounced)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem(scrollKey, window.scrollY.toString());
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [id, location.state?.restoredFromHistory]);


  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return 'Unknown source';
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await removeArticle(currentArticle.id);
        goBack();
      } catch (error) {
        console.error('Error deleting article:', error);
      }
    }
  };

  const handleMarkAsRead = async () => {
    try {
      await markAsRead(currentArticle.id);
      goBack();
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsReadStay = async () => {
    try {
      await markAsRead(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      await markAsUnread(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as unread:', error);
    }
  };


  const handleRegenerateSummary = async () => {
    try {
      setLocalError(null);
      await regenerateArticleSummary(currentArticle.id);
    } catch (error) {
      console.error('Error regenerating summary:', error);
      setLocalError(error.message || 'Failed to regenerate summary');
    }
  };

  const handleRefetchContent = async () => {
    try {
      setLocalError(null);
      await refetchArticleContent(currentArticle.id);
    } catch (error) {
      console.error('Error refetching content:', error);
      setLocalError(error.message || 'Failed to refetch content');
    }
  };

  // Feedback functionality
  const fetchExistingFeedback = async (articleId) => {
    try {
      const feedbackData = await getArticleFeedback(articleId);
      setFeedback(feedbackData);
    } catch (error) {
      // No feedback exists yet, which is fine
      setFeedback(null);
    }
  };

  const handleFeedback = async (feedbackValue) => {
    if (feedbackLoading) return;
    
    setFeedbackLoading(true);
    const previousFeedback = feedback;
    
    // Optimistic update
    setFeedback(prev => ({
      id: prev?.id || Date.now(), // Temporary ID for new feedback
      user_id: prev?.user_id || 0, // Will be set by API response
      article_id: currentArticle.id,
      feedback_value: feedbackValue,
      created_at: new Date().toISOString(),
      ...prev // Keep any existing fields, but override above
    }));

    try {
      const response = await createArticleFeedback(currentArticle.id, feedbackValue);
      setFeedback(response);

      // Toast removed - was causing crashes
    } catch (error) {
      // Revert optimistic update on error
      setFeedback(previousFeedback);
      console.error('Error creating feedback:', error);
      // Toast removed - was causing crashes
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Share functionality handlers
  const handleShareClick = () => {
    // Capture the current selection before clearing it
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection(); // Clear UI elements but preserve captured data
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    // Clear captured selection when dialog is closed
    setCapturedSelection({
      text: '',
      markdown: '',
      hasMarkdown: false
    });
  };

  // Highlight functionality handlers
  const handleCreateHighlight = async () => {
    // Get the rendered text from the DOM
    const summaryContainer = document.querySelector('[data-markdown-source]');
    const renderedText = summaryContainer?.textContent || '';

    if (!selectedText || !currentArticle?.summary) {
      return;
    }

    try {
      setIsCreatingHighlight(true);
      await createHighlight(selectedText, null, renderedText);
      hideSelection();
    } catch (error) {
      console.error('Error creating highlight:', error);
      setLocalError(error.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  const handleHighlightClick = (highlight) => {
    // Scroll to highlight in the article (future enhancement)
    console.log('Highlight clicked:', highlight);
  };

  const handleDeleteHighlight = async (highlightId) => {
    try {
      await deleteHighlight(highlightId);
    } catch (error) {
      console.error('Error deleting highlight:', error);
      setLocalError(error.message || 'Failed to delete highlight');
    }
  };

  const handleReattachHighlights = async () => {
    try {
      await reattachHighlights(0.8);
    } catch (error) {
      console.error('Error re-attaching highlights:', error);
      setLocalError(error.message || 'Failed to re-attach highlights');
    }
  };

  const readingTime = currentArticle?.content ? 
    Math.ceil(currentArticle.content.split(' ').length / 200) : 0;

  // Component for feedback buttons
  const FeedbackButtons = () => {
    return (
      <div className="flex justify-center pt-6 mt-8 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 mr-3">Rate this article:</span>

          {/* Thumbs Down */}
          <button
            onClick={() => handleFeedback(-1)}
            disabled={feedbackLoading}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              feedback?.feedback_value === -1
                ? 'bg-red-100 text-red-600'
                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Poor quality"
          >
            {feedbackLoading && feedback?.feedback_value === -1 ? (
              <div className="spinner w-5 h-5"></div>
            ) : (
              <span className="text-xl">👎</span>
            )}
          </button>

          {/* Neutral */}
          <button
            onClick={() => handleFeedback(0)}
            disabled={feedbackLoading}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              feedback?.feedback_value === 0
                ? 'bg-yellow-100 text-yellow-600'
                : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Alright"
          >
            {feedbackLoading && feedback?.feedback_value === 0 ? (
              <div className="spinner w-5 h-5"></div>
            ) : (
              <span className="text-xl">😐</span>
            )}
          </button>

          {/* Thumbs Up */}
          <button
            onClick={() => handleFeedback(1)}
            disabled={feedbackLoading}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              feedback?.feedback_value === 1
                ? 'bg-green-100 text-green-600'
                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Good quality"
          >
            {feedbackLoading && feedback?.feedback_value === 1 ? (
              <div className="spinner w-5 h-5"></div>
            ) : (
              <span className="text-xl">👍</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Component for the "Mark as Read" button at article end
  const MarkReadButton = () => {
    if (currentArticle.is_read) {
      return (
        <div className="flex justify-center pt-8">
          <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
            <div className="flex items-center justify-center w-4 h-4 mr-2 bg-gray-300 rounded-full">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            Article Read
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center gap-3 pt-8">
        {/* Done Reading - navigates away */}
        <button
          onClick={handleMarkAsRead}
          disabled={isUpdating}
          className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-800 rounded-lg text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          title="Mark as read and return to dashboard"
        >
          {isUpdating ? (
            <>
              <div className="spinner w-4 h-4 mr-2"></div>
              Finishing...
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-4 h-4 mr-2 border border-gray-400 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              Done reading
            </>
          )}
        </button>

        {/* Mark as Read - stays on page */}
        <button
          onClick={handleMarkAsReadStay}
          disabled={isUpdating}
          className="inline-flex items-center justify-center px-3 py-2 bg-transparent hover:bg-gray-100 text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          title="Mark as read (stay on page)"
        >
          {isUpdating ? (
            <div className="spinner w-4 h-4"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  const tabs = [
    {
      id: 'summary',
      label: 'Summary',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'content',
      label: 'Content',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      id: 'original',
      label: 'Original',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Only show full-page error if article failed to load (not during operations)
  if (localError && !currentArticle && !isRegenerating && !isRefetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Article</h2>
          <p className="text-gray-600 mb-4">{localError}</p>
          <button
            onClick={goBack}
            className="btn-primary"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!currentArticle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={goBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="flex items-center space-x-2">
              {/* Highlights button */}
              <button
                onClick={() => setHighlightsSidebarOpen(true)}
                className="relative p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                title="View highlights"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                {highlights && highlights.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                    {highlights.length}
                  </span>
                )}
              </button>

              {/* Done/Undone button */}
              <button
                onClick={currentArticle.is_read ? handleMarkAsUnread : handleMarkAsRead}
                disabled={isUpdating}
                className={`p-2 rounded-lg transition-colors duration-200 ${
                  currentArticle.is_read
                    ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                    : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                }`}
                title={currentArticle.is_read ? "Mark as unread" : "Mark as read"}
              >
                {isUpdating ? (
                  <div className="spinner w-5 h-5"></div>
                ) : currentArticle.is_read ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>

              {/* Delete button */}
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Delete article"
              >
                {isDeleting ? (
                  <div className="spinner w-5 h-5"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner - shown inline when operations fail */}
      {localError && currentArticle && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm text-red-800">{localError}</p>
              </div>
              <button
                onClick={() => setLocalError(null)}
                className="text-red-600 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-4">
            <div className="flex items-center text-sm text-gray-500 mb-2">
              <span className="bg-gray-100 px-2 py-1 rounded-full mr-3">
                {getDomainFromUrl(currentArticle.url)}
              </span>
              {/* Read status indicator */}
              {currentArticle.is_read && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full mr-3 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Read
                </span>
              )}
              {/* Language indicator */}
              {shouldShowLanguageBadge(currentArticle.language) && (
                <span 
                  className={`${getLanguageBadgeClasses(currentArticle.language)} mr-3`}
                  title={`Article language: ${getLanguageName(currentArticle.language)}`}
                >
                  {formatLanguageCode(currentArticle.language)}
                </span>
              )}
              <time dateTime={currentArticle.publish_date || currentArticle.created_at}>
                {formatFullDate(currentArticle.publish_date || currentArticle.created_at)}
              </time>
              {readingTime > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <span>{readingTime} min read</span>
                </>
              )}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {currentArticle.title || 'Untitled Article'}
          </h1>

          {/* Author, Domain, and Date */}
          {(currentArticle.author || currentArticle.domain || currentArticle.publish_date || currentArticle.created_at) && (
            <div className="flex flex-wrap items-center text-sm text-gray-600 mb-4 gap-4">
              {currentArticle.author && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{currentArticle.author}</span>
                </div>
              )}
              {currentArticle.domain && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>{currentArticle.domain}</span>
                </div>
              )}
              {(currentArticle.publish_date || currentArticle.created_at) && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{formatFullDate(currentArticle.publish_date || currentArticle.created_at)}</span>
                </div>
              )}
            </div>
          )}

          {/* Status indicator */}
          <div className="flex items-center mb-4">
            {currentArticle.status === 'processing' && (
              <div className="flex items-center text-amber-600">
                <div className="spinner w-4 h-4 mr-2"></div>
                <span className="text-sm">Processing article...</span>
              </div>
            )}
            
            {currentArticle.status === 'failed' && (
              <div className="flex items-center text-red-600">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Failed to process article</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'summary' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {currentArticle.summary ? (
              <div>
                {/* Re-summarize button */}
                <div className="flex justify-end mb-6">
                  <button
                    onClick={handleRegenerateSummary}
                    disabled={isRegenerating}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    title="Generate a new summary for this article"
                  >
                    {isRegenerating ? (
                      <>
                        <div className="spinner w-4 h-4 mr-2"></div>
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Re-summarize
                      </>
                    )}
                  </button>
                </div>
                
                {/* Summary content with highlight overlay */}
                <HighlightRenderer
                  text={null}
                  highlights={highlights}
                  onHighlightClick={handleHighlightClick}
                >
                  <div
                    className="prose prose-lg max-w-none"
                    dir={getTextDirection(currentArticle.language)}
                    data-markdown-source={currentArticle.summary}
                    data-markdown={currentArticle.summary}
                  >
                    {hasMarkdownFormatting(currentArticle.summary) ? (
                      <div className="space-y-2">
                        {parseMarkdownSummary(currentArticle.summary)}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {currentArticle.summary}
                      </div>
                    )}
                  </div>
                </HighlightRenderer>

                {/* Image Debug Panel (dev only) */}
                {currentArticle.debug_data && (
                  <ImageDebugPanel
                    debugData={currentArticle.debug_data}
                    threshold={0.08}
                  />
                )}

                {/* Feedback buttons */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />

                {/* Related Articles Widget */}
                <RelatedArticlesWidget
                  articleId={parseInt(id)}
                  currentArticle={currentArticle}
                />
              </div>
            ) : (
              <div>
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mb-4">No summary available</p>
                  {currentArticle.status === 'processing' ? (
                    <p className="text-sm mt-2">Summary will be generated shortly...</p>
                  ) : (
                    <button
                      onClick={handleRegenerateSummary}
                      disabled={isRegenerating}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isRegenerating ? (
                        <>
                          <div className="spinner w-4 h-4 mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Generate Summary
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Feedback buttons - available even without summary */}
                <FeedbackButtons />
                
                {/* Mark as read button */}
                <MarkReadButton />
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {currentArticle.content ? (
              <div>
                {/* Re-fetch content button */}
                <div className="flex justify-end mb-6">
                  <button
                    onClick={handleRefetchContent}
                    disabled={isRefetching}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    title="Re-fetch the latest content from the original URL"
                  >
                    {isRefetching ? (
                      <>
                        <div className="spinner w-4 h-4 mr-2"></div>
                        Re-fetching...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Re-fetch Content
                      </>
                    )}
                  </button>
                </div>
                
                {/* Content display */}
                <div 
                  className="prose prose-lg max-w-none"
                  dir={getTextDirection(currentArticle.language)}
                  data-markdown-source={currentArticle.content}
                  data-markdown={currentArticle.content}
                >
                  {hasMarkdownFormatting(currentArticle.content) ? (
                    <div className="space-y-2">
                      {parseMarkdownSummary(currentArticle.content)}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {currentArticle.content}
                    </div>
                  )}
                </div>

                {/* Feedback buttons */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />

                {/* Related Articles Widget */}
                <RelatedArticlesWidget
                  articleId={parseInt(id)}
                  currentArticle={currentArticle}
                />
              </div>
            ) : (
              <div>
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <p className="mb-4">No content available</p>
                  {currentArticle.status === 'processing' ? (
                    <p className="text-sm mt-2">Content is being extracted...</p>
                  ) : (
                    <button
                      onClick={handleRefetchContent}
                      disabled={isRefetching}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isRefetching ? (
                        <>
                          <div className="spinner w-4 h-4 mr-2"></div>
                          Fetching...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Fetch Content
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Feedback buttons - available even without content */}
                <FeedbackButtons />
                
                {/* Mark as read button */}
                <MarkReadButton />
              </div>
            )}
          </div>
        )}

        {activeTab === 'original' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="mb-6">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  View Original Article
                </h3>
                <p className="text-gray-600 mb-6">
                  Visit the original source to read the full article with all formatting, images, and interactive elements.
                </p>
              </div>
              
              <a
                href={currentArticle.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open Original Article
              </a>
              
              <div className="mt-4 text-sm text-gray-500">
                <span className="break-all">{currentArticle.url}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share and Highlight Components */}
      {selectedText && (
        <>
          {/* Context Menu (for right-click) */}
          <ShareContextMenu
            visible={contextMenuVisible}
            position={contextMenuPosition}
            onShare={handleShareClick}
            onHighlight={handleCreateHighlight}
            onClose={hideSelection}
            hasMarkdown={hasMarkdown}
            showHighlight={activeTab === 'summary'}
            isHighlighting={isCreatingHighlight}
            ref={contextMenuRef}
          />
        </>
      )}

      {/* Share Preview Dialog */}
      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={currentArticle?.title}
        articleUrl={currentArticle?.url}
      />

      {/* Processing Toast - non-blocking notification */}
      {(isRegenerating || isRefetching) && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
            <div className="flex items-start">
              <div className="spinner w-5 h-5 mr-3 mt-0.5 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  {isRefetching ? 'Re-fetching Content...' : 'Regenerating Summary...'}
                </h4>
                <p className="text-xs text-gray-600">
                  {isRefetching
                    ? 'This may take 1-5 minutes. Feel free to navigate away.'
                    : 'This may take 30-120 seconds. Feel free to navigate away.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Highlights Sidebar */}
      <HighlightsSidebar
        highlights={highlights}
        isOpen={highlightsSidebarOpen}
        onClose={() => setHighlightsSidebarOpen(false)}
        onHighlightClick={handleHighlightClick}
        onDeleteHighlight={handleDeleteHighlight}
        onDeleteAll={deleteAllHighlights}
        detachmentInfo={detachmentInfo}
        onReattach={handleReattachHighlights}
        isLoading={highlightsLoading}
      />
    </div>
  );
};

export default ArticleViewer;